import * as React from "react";
import { MenuList } from "./layout/list";
import { MenuListButton } from "./layout/button";
import { MenuListItem } from "./layout/item";
import shortid from "shortid";
import cloneDeep from "lodash/cloneDeep";
import { Modal } from "@lib/components";
import { ModalContent } from "@lib/components";
import { Button } from "@lib/components";
import { Tag } from "@lib/components";
import { FormTreeSelect } from "@lib/components";
import { RotateLoader } from "@lib/components";
import { DragDropContext, DropResult } from "react-beautiful-dnd";
import { arrayMove } from "react-sortable-hoc";
import { RestaurantFormOptionSet } from "./forms/option_set";
import { inject, observer } from "mobx-react";
import { MobxComponent } from "../../../../../mobx/components";
import {
	buildOptionSetAttachUpdates,
	buildOptionSetDetachAllUpdates,
	buildOptionSetDetachUpdates,
	getDishIdsWithOptionSets,
	logger,
	RestaurantUtils,
} from "@lib/common";
import { UI } from "../../../../../core/ui";
import {
	MenuListFloatingButtonWrapper,
	MenuListFloatingContent,
	MenuItemText,
	MenuListFloatingTitle,
} from "./layout/floating-button";
import { BulkConfirmModal } from "../../../../components/v2/bulk-confirm-modal";
import { applyMenuListView, MenuListSortDirection, sortByName } from "./utils/sortLists";

interface Props {
	filterText?: string;
	sortDirection?: MenuListSortDirection;
	onSortDirectionChange?: (direction: MenuListSortDirection) => void;
}
type BulkAction = "delete" | "hide" | "available" | "attach" | "detach";
interface State {
	reorder: boolean;
	index_copy: number | null;
	index_panel: number | null;
	index_remove: number | null;

	loading_copy: boolean;
	loading_remove: boolean;
	loading_reorder: boolean;
	loading_list_sort: boolean;
	selected_ids: string[];
	selected_loading: boolean;
	bulk_action: BulkAction | null;
	attach_selected_dishes: string[];
}

@inject("store")
@observer
export class RestaurantOptionSetList extends MobxComponent<Props, State> {
	backup: T.Schema.Restaurant.RestaurantSchema["option_sets"] | null;

	constructor(props: Props) {
		super(props);
		this.state = {
			reorder: false,
			index_copy: null,
			index_panel: null,
			index_remove: null,
			loading_copy: false,
			loading_remove: false,
			loading_reorder: false,
			loading_list_sort: false,
			selected_ids: [],
			selected_loading: false,
			bulk_action: null,
			attach_selected_dishes: [],
		};
		this.backup = null;
	}

	componentWillUnmount() {
		if (this.state.reorder) {
			this.reorder_cancel();
		}
	}

	create_item = (): T.Schema.Restaurant.Menu.RestaurantOptionSet => {
		return {
			_id: shortid.generate(),
			name: "",
			display_name: "",
			show_in_menu: false,
			disabled: false,
			conditions: {
				required: false,
				multi_select: false,
				quantity_select: false,
				min_options: "",
				max_options: "",
				free_amount: "",
			},
			options: [],
		};
	};

	getDisplayOptionSets = () => {
		const r = this.injected.store.restaurant!;
		const { reorder } = this.state;
		if (reorder) return r.option_sets;
		return applyMenuListView(r.option_sets, {
			filterText: this.props.filterText,
			sortDirection: this.props.sortDirection ?? null,
		});
	};

	// Toolbar A–Z / Z–A is a view until the user clicks Save; Save persists the
	// sorted order the same way Re-arrange does ($set option_sets).
	cancelListSort = () => {
		this.props.onSortDirectionChange?.(null);
	};

	saveListSort = async () => {
		const direction = this.props.sortDirection;
		if (!direction) return;
		const { store } = this.injected;
		const r = store.restaurant!;
		const backup = cloneDeep(r);
		const sorted = sortByName(r.option_sets || [], direction);
		// Optimistic, like reorder_save; saveRestaurantSilent restores `backup` on failure.
		store.setRestaurant({ ...r, option_sets: sorted });
		await this.saveRestaurantSilent({
			backup,
			update: { $set: { option_sets: sorted } },
			successMsg: "Option sets sorted",
			before: () => this.setState({ loading_list_sort: true }),
			onSuccess: () => {
				this.setState({ loading_list_sort: false });
				this.props.onSortDirectionChange?.(null);
			},
			onFail: () => this.setState({ loading_list_sort: false }),
			onError: () => this.setState({ loading_list_sort: false }),
		});
	};

	listSortSaveCancel = () => {
		const { loading_list_sort } = this.state;
		return (
			<div className="grid-2 xs xs-gap m-t-10">
				<div className="col">
					<MenuListButton onClick={this.saveListSort}>
						{loading_list_sort && <RotateLoader size={2} color="white" />}
						{!loading_list_sort && "Save"}
					</MenuListButton>
				</div>
				<div className="col">
					<MenuListButton onClick={this.cancelListSort}>Cancel</MenuListButton>
				</div>
			</div>
		);
	};

	list_item = () => {
		const { reorder, loading_reorder, selected_ids } = this.state;
		const pendingListSort = !reorder && !!this.props.sortDirection && !!this.props.onSortDirectionChange;
		const r = this.injected.store.restaurant!;
		const option_sets = this.getDisplayOptionSets();
		// index lookup built once per render instead of a findIndex per row (B4)
		const indexById = new Map(r.option_sets.map((optionSet, index) => [optionSet._id, index]));
		return (
			<>
				<MenuList id="option-set-list" type="OPTIONSET">
					{option_sets.map((item, i) => {
						const originalIndex = indexById.get(item._id) ?? -1;
						return (
							<MenuListItem
								key={item._id}
								type="OPTIONSET"
								id={item._id}
								mode={reorder ? "reOrder" : "normal"}
								name={
									<div className="flex-l-r-center width100">
										<MenuItemText className="m-l-3" style={{maxWidth: '40vw'}}>
											{item.name + (item.using_points === true ? " 🍕" : "")}
										</MenuItemText>
										{item.disabled === true && <Tag className="m-r-2">Hidden</Tag>}
									</div>
								}
								index={reorder ? i : originalIndex}
								onEdit={() => this.panel_set(originalIndex)}
								onRemove={() => this.modal_remove_open(originalIndex)}
								onCopy={() => this.modal_copy_open(originalIndex)}
								selectable={!reorder}
								selected={selected_ids.indexOf(item._id) !== -1}
								onToggleSelect={this.toggle_selected_option_set}
							/>
						);
					})}
				</MenuList>

				{pendingListSort && this.listSortSaveCancel()}

				{!reorder && !pendingListSort && (
					<div className="grid-2 xs xs-gap m-t-10">
						<div className="col">
							<MenuListButton
								key="btn-create"
								onClick={() => this.panel_set(-1)}
							>
								Create New Option Set
							</MenuListButton>
						</div>
						<div className="col">
							<MenuListButton
								key="btn-arrange"
								onClick={this.reorder_start}
							>
								Re-arrange
							</MenuListButton>
						</div>
					</div>
				)}

				{reorder && (
					<>
						<div className="grid-2 xs xs-gap m-t-10">
							<div className="col">
								<MenuListButton onClick={this.reorder_save}>
									{loading_reorder && (
										<RotateLoader size={2} color="white" />
									)}
									{!loading_reorder && "Save"}
								</MenuListButton>
							</div>
							<div className="col">
								<MenuListButton onClick={this.reorder_cancel}>
									Cancel
								</MenuListButton>
							</div>
						</div>
						<div className="text-center m-t-3">
							<p>
								Click + hold to drag and drop items to re-order
								them
							</p>
						</div>
					</>
				)}
			</>
		);
	};

	panel_set = (index_panel: number | null) => {
		this.setState({ index_panel });
	};
	panel_create = () => {
		const r = this.injected.store.restaurant!;
		const { index_panel } = this.state;

		let initialValues = null;
		if (index_panel !== null) {
			if (index_panel === -1) {
				initialValues = this.create_item();
			} else {
				const item = r.option_sets[index_panel];
				initialValues = cloneDeep(item);
			}
		}

		return (
			<RestaurantFormOptionSet
				type={index_panel === -1 ? "create" : "edit"}
				initialValues={initialValues}
				close={() => this.panel_set(null)}
			/>
		);
	};

	copy = async (index: number) => {
		await this.saveRestaurant({
			successMsg: "Option set copied",
			process: (r) => {
				const optionSet = cloneDeep(r.option_sets[index]);
				optionSet._id = shortid.generate();
				optionSet.name = `${optionSet.name} - Copy`;
				for (const option of optionSet.options) {
					option._id = shortid.generate();
				}
				r.option_sets.splice(index + 1, 0, optionSet);
				return {
					update: { $set: { option_sets: r.option_sets } },
				};
			},
			before: () => this.setState({ loading_copy: true }),
			onSuccess: this.modal_copy_close,
			onFail: () => this.setState({ loading_copy: false }),
			onError: () => this.setState({ loading_copy: false }),
		});
	};
	remove = async (index: number) => {
		const optionSet = cloneDeep(this.injected.store.restaurant!.option_sets[index]);
		await this.saveRestaurant({
			successMsg: "Option set deleted",
			process: (r) => {
				const updates = buildOptionSetDetachAllUpdates(r.menus, [optionSet._id]);
				this.apply_dish_option_set_updates(r.menus, updates);
				r.option_sets.splice(index, 1);
				return {
					update: {
						$set: { option_sets: r.option_sets, menus: r.menus },
					},
				};
			},
			before: () => this.setState({ loading_remove: true }),
			onSuccess: () => {
				this.clear_option_set_stock([optionSet]);
				this.modal_remove_close();
			},
			onFail: () => this.setState({ loading_remove: false }),
			onError: () => this.setState({ loading_remove: false }),
		});
	};

	toggle_selected_option_set = (optionSetId: string) => {
		const selected_ids = [...this.state.selected_ids];
		const index = selected_ids.indexOf(optionSetId);
		if (index === -1) selected_ids.push(optionSetId);
		else selected_ids.splice(index, 1);
		this.setState({ selected_ids });
	};

	select_all = () => {
		this.setState({
			selected_ids: this.getDisplayOptionSets().map((optionSet) => optionSet._id),
		});
	};

	clear_selection = () => {
		this.setState({ selected_ids: [] });
	};

	open_bulk_action = (bulk_action: BulkAction) => {
		const restaurant = this.injected.store.restaurant!;
		const attach_selected_dishes =
			bulk_action === "detach"
				? getDishIdsWithOptionSets(restaurant.menus || [], this.state.selected_ids)
				: [];
		this.setState({ bulk_action, attach_selected_dishes });
	};

	close_bulk_action = () => {
		this.setState({
			bulk_action: null,
			selected_loading: false,
			attach_selected_dishes: [],
			selected_ids: [],
		});
	};

	clear_option_set_stock = (
		optionSets: T.Schema.Restaurant.Menu.RestaurantOptionSet[]
	) => {
		const { store } = this.injected;
		const items = optionSets
			.reduce(
				(list, optionSet) => [
					...list,
					...(optionSet.options || []),
				],
				[] as T.Schema.Restaurant.Menu.RestaurantOptionSetOption[]
			)
			.map((option) => ({
				_id: option._id,
				key: "option_set_options" as "option_set_options",
				value: "" as "",
			}));
		if (items.length === 0) return;
		store.api
			.menu_stock_update({
				_id: store.restaurant!._id,
				items,
			})
			.then((result) => {
				store.setRestaurantStock(result.stock);
			})
			.catch((e) => {
				logger.captureException(e);
				UI.notification.error(
					"Option sets updated, but clearing their stock failed. Please refresh and check stock."
				);
			});
	};

	apply_dish_option_set_updates = (
		menus: T.Schema.Restaurant.Menu.RestaurantMenu[],
		updates: Array<{
			id: string;
			option_sets: string[];
		}>
	) => {
		const optionSetsByDishId = new Map(
			updates.map((update) => [update.id, update.option_sets])
		);
		for (const menu of menus) {
			for (const category of menu.categories) {
				for (const dish of category.dishes) {
					const optionSets = optionSetsByDishId.get(dish._id);
					if (optionSets) dish.option_sets = optionSets;
				}
			}
		}
	};

	apply_bulk_action = async () => {
		const { bulk_action, selected_ids, attach_selected_dishes } = this.state;
		if (!bulk_action || selected_ids.length === 0) return;
		const selectedIdSet = new Set(selected_ids);
		const optionSetsToDelete = cloneDeep(
			this.injected.store.restaurant!.option_sets.filter(
				(optionSet) =>
					bulk_action === "delete" &&
					selectedIdSet.has(optionSet._id)
			)
		);

		await this.saveRestaurant({
			successMsg: this.bulk_action_success_message(bulk_action),
			process: (r) => {
				const update: T.ObjectAny = {};

				if (bulk_action === "hide" || bulk_action === "available") {
					const disabled = bulk_action === "hide";
					for (const optionSet of r.option_sets) {
						if (selectedIdSet.has(optionSet._id)) {
							optionSet.disabled = disabled;
						}
					}
					update.option_sets = r.option_sets;
				}

				if (
					bulk_action === "attach" ||
					bulk_action === "detach" ||
					bulk_action === "delete"
				) {
					let updates;
					if (bulk_action === "attach") {
						updates = buildOptionSetAttachUpdates(
							r.menus,
							selected_ids,
							attach_selected_dishes
						);
					} else if (bulk_action === "detach") {
						updates = buildOptionSetDetachUpdates(
							r.menus,
							selected_ids,
							attach_selected_dishes
						);
					} else {
						updates = buildOptionSetDetachAllUpdates(
							r.menus,
							selected_ids
						);
					}
					this.apply_dish_option_set_updates(r.menus, updates);
					update.menus = r.menus;
				}

				if (bulk_action === "delete") {
					r.option_sets = r.option_sets.filter(
						(optionSet) => !selectedIdSet.has(optionSet._id)
					);
					update.option_sets = r.option_sets;
				}

				return { update: { $set: update } };
			},
			before: () => this.setState({ selected_loading: true }),
			onSuccess: () => {
				if (optionSetsToDelete.length > 0) {
					this.clear_option_set_stock(optionSetsToDelete);
				}
				this.setState({
					selected_ids: [],
					selected_loading: false,
					bulk_action: null,
					attach_selected_dishes: [],
				});
			},
			onFail: () => this.setState({ selected_loading: false }),
			onError: () => this.setState({ selected_loading: false }),
		});
	};

	bulk_action_title = (bulk_action: BulkAction) => {
		const count = this.state.selected_ids.length;
		if (bulk_action === "delete") return `Delete ${count} option sets`;
		if (bulk_action === "hide") return `Hide ${count} option sets`;
		if (bulk_action === "available") return `Make ${count} option sets available`;
		if (bulk_action === "attach") return `Attach ${count} option sets`;
		return `Detach ${count} option sets`;
	};

	bulk_action_description = (bulk_action: BulkAction) => {
		if (bulk_action === "delete") return "This will permanently delete the selected option sets.";
		if (bulk_action === "hide") return "Hidden option sets will not show on the store or in item selection.";
		if (bulk_action === "available") return "Available option sets will show on the store and in item selection.";
		if (bulk_action === "attach") return "Choose the items these selected option sets should be attached to.";
		return "Choose the items these selected option sets should be detached from.";
	};

	bulk_action_success_message = (bulk_action: BulkAction) => {
		if (bulk_action === "delete") return "Option sets deleted";
		if (bulk_action === "hide") return "Option sets hidden";
		if (bulk_action === "available") return "Option sets available";
		if (bulk_action === "attach") return "Option sets attached";
		return "Option sets detached";
	};

	modal_copy_open = (index: number | null) => {
		this.setState({
			index_copy: index,
			loading_copy: false,
		});
	};
	modal_copy_close = () => {
		this.setState({
			index_copy: null,
			loading_copy: false,
		});
	};
	modal_remove_open = (index: number | null) => {
		this.setState({
			index_remove: index,
			loading_remove: false,
		});
	};
	modal_remove_close = () => {
		this.setState({
			index_remove: null,
			loading_remove: false,
		});
	};

	modal_copy = () => {
		const r = this.injected.store.restaurant!;
		const { index_copy, loading_copy } = this.state;

		let item;
		let fn;
		if (index_copy !== null) {
			item = r.option_sets[index_copy];
			fn = () => this.copy(index_copy);
		}

		return (
			<Modal
				width="sm"
				close={this.modal_copy_close}
				closeButton={false}
				active={!!item}
			>
				<ModalContent className="flex-l-r-center">
					<h4 className="">Copy Option Set</h4>
					<p className="big underline notranslate">{item && item.name}</p>
				</ModalContent>
				<ModalContent className="flex-right">
					<Button
						type="button"
						className="m-r-2 width100 max100px"
						onClick={this.modal_copy_close}
					>
						Cancel
					</Button>
					<Button
						type="button"
						className="width100 max100px"
						color="primary"
						onClick={fn}
					>
						{loading_copy && (
							<RotateLoader size={2} color="white" />
						)}
						{!loading_copy && "Copy"}
					</Button>
				</ModalContent>
			</Modal>
		);
	};
	modal_remove = () => {
		const r = this.injected.store.restaurant!;
		const { index_remove, loading_remove } = this.state;

		let item;
		let fn;
		if (index_remove !== null) {
			item = r.option_sets[index_remove];
			fn = () => this.remove(index_remove);
		}

		return (
			<Modal
				width="sm"
				close={this.modal_remove_close}
				closeButton={false}
				active={!!item}
			>
				<ModalContent className="flex-l-r-center">
					<h4 className="">Delete Option Set</h4>
					<p className="big underline notranslate">{item && item.name}</p>
				</ModalContent>
				<ModalContent className="flex-right">
					<Button
						type="button"
						className="m-r-2 width100 max100px"
						onClick={this.modal_remove_close}
					>
						Cancel
					</Button>
					<Button
						type="button"
						className="width100 max100px"
						color="primary"
						onClick={fn}
					>
						{loading_remove && (
							<RotateLoader size={2} color="white" />
						)}
						{!loading_remove && "Delete"}
					</Button>
				</ModalContent>
			</Modal>
		);
	};

	bulk_action_modal = () => {
		const { bulk_action, selected_loading, attach_selected_dishes } = this.state;
		const restaurant = this.injected.store.restaurant!;
		if (!bulk_action) return null;
		const isItemPicker = bulk_action === "attach" || bulk_action === "detach";

		return (
			<BulkConfirmModal
				active={true}
				title={this.bulk_action_title(bulk_action)}
				description={this.bulk_action_description(bulk_action)}
				loading={selected_loading}
				confirmDisabled={isItemPicker && attach_selected_dishes.length === 0}
				onClose={this.close_bulk_action}
				onConfirm={this.apply_bulk_action}
			>
				{isItemPicker && (
					<div style={{ width: "100%", maxHeight: 240, overflowY: "auto" }}>
						<FormTreeSelect
							id="bulk-attach-option-sets"
							selected={attach_selected_dishes}
							nodes={RestaurantUtils.menu.getTreeFilter(
								restaurant,
								(d) => d.type !== "combo"
							)}
							onChange={(dishIds) => this.setState({ attach_selected_dishes: dishIds })}
						/>
					</div>
				)}
			</BulkConfirmModal>
		);
	};

	bulk_action_bar = () => {
		const { selected_ids, selected_loading } = this.state;
		return (
			<MenuListFloatingButtonWrapper>
				<MenuListFloatingContent>
					<MenuListFloatingTitle>
						<p className="font-semi-bold big">Actions</p>
						<p className="font-semi-bold small m-t-1">
							{selected_ids.length} Selected
						</p>
					</MenuListFloatingTitle>
					<Button size="xs" onClick={this.clear_selection}>
						Cancel
					</Button>
					<Button size="xs" onClick={this.select_all}>
						Select All
					</Button>
					<Button size="xs" onClick={() => this.open_bulk_action("available")}>
						Available
					</Button>
					<Button size="xs" onClick={() => this.open_bulk_action("hide")}>
						Hide
					</Button>
					<Button size="xs" onClick={() => this.open_bulk_action("attach")}>
						Attach
					</Button>
					<Button size="xs" onClick={() => this.open_bulk_action("detach")}>
						Detach
					</Button>
					<Button size="xs" onClick={() => this.open_bulk_action("delete")}>
						Delete
					</Button>
					{selected_loading && (
						<div className="text-center p-1">
							<RotateLoader size={2} />
						</div>
					)}
				</MenuListFloatingContent>
			</MenuListFloatingButtonWrapper>
		);
	};

	reorder_start = () => {
		this.setState({ reorder: true });
		const r = this.injected.store.restaurant!;
		this.backup = cloneDeep(r.option_sets);
	};
	reorder_cancel = () => {
		const r = this.injected.store.restaurant!;
		r.option_sets = this.backup || [];
		this.setState({ reorder: false });
	};
	reorder_save = async () => {
		const r = this.injected.store.restaurant!;
		const backup = cloneDeep(r);
		if (this.backup) backup.option_sets = this.backup;
		await this.saveRestaurantSilent({
			backup: backup,
			update: { $set: { option_sets: r.option_sets } },
			successMsg: "Option sets re-arranged",
			before: () => this.setState({ loading_reorder: true }),
			onSuccess: () => {
				this.setState({ reorder: false, loading_reorder: false });
				this.backup = null;
			},
			onFail: () => this.setState({ loading_reorder: false }),
			onError: () => this.setState({ loading_reorder: false }),
		});
	};

	onDragEnd = (result: DropResult) => {
		const { destination, source } = result;
		if (!destination) return;
		const r = { ...this.injected.store.restaurant! };
		if (destination.index !== source.index) {
			r.option_sets = arrayMove(
				r.option_sets,
				source.index,
				destination.index
			);
			this.injected.store.setRestaurant(r);
		}
	};

	render() {
		const { selected_ids } = this.state;
		return (
			<div className="relative">
				{selected_ids.length > 0 && this.bulk_action_bar()}
				<DragDropContext onDragEnd={this.onDragEnd}>
					{this.list_item()}
					{this.panel_create()}
					{this.modal_copy()}
					{this.modal_remove()}
					{this.bulk_action_modal()}
				</DragDropContext>
			</div>
		);
	}
}
